/* sc-image-resolver.js
 * Resolves product images from configuration selections.
 *
 * Uses SKU-based flat filenames on Cloudflare R2.
 * All renders live in one folder with filenames like:
 *   KCOS25K-O1421-FR.png
 *   KCOS25K-O1421-SI.png
 *
 * The resolver constructs the SKU from the configurator state,
 * appends the view code, and builds the CDN URL.
 *
 * Fallback chain:
 * 1. Exact SKU + view
 * 2. Same range/size but in Snow Drop (O14) with default cover/cushion/piping
 * 3. Legacy sc-images.js lookup
 * 4. Placeholder
 */
(function() {
  'use strict';

  window.SC = window.SC || {};

  /* ── SKU encoding tables ── */
  var RANGE_CODES = { coco:'CO', felix:'FE', isobel:'IS', barton:'BA', henrietta:'HE', sophia:'SO', salthrop:'SA', cocobella:'CB', 'felix-jl':'FJ' };
  var SIZE_CODES = { 'armchair':'10', '1-seater':'10', 'loveseat':'15', '1.5-seater':'15', '2-seater':'20', '2.5-seater':'25', '3-seater':'30', '3.5-seater':'35', '4-seater':'40',
    /* John Lewis size names, mapped onto the same underlying codes.
       'snuggler' does NOT appear in the SKU reference; 15 (1.5 seater)
       is the assumed equivalent and needs confirming. Without an entry
       it would fall back to 25 silently, which would be worse. */
    'small':'20', 'medium':'25', 'large':'30', 'grand':'40', 'snuggler':'15' };
  /* John Lewis size names map to different codes per range: Cocobella
     Grand is 35 (not 40) and Salthrop Medium is 20 (not 25). Taken from
     the render manifest, so the SKU matches a real file. Sectionals sit
     in the Modular category (M) and carry handedness in the VIEW slot
     (LHF / RHF), not the size, so both 2x3 share the size code LA. */
  var RANGE_SIZE_CODES = {
    cocobella: { armchair:'10', snuggler:'15', small:'20', medium:'25', large:'30', grand:'35',
                 'corner-3x3':'33', 'left-hand-2x3':'LA', 'right-hand-2x3':'LA' },
    salthrop:  { snuggler:'15', medium:'20', large:'30' },
    'felix-jl':{ armchair:'10', medium:'25', large:'30' }
  };
  var MODULAR_SIZES = { 'corner-3x3':1, 'left-hand-2x3':1, 'right-hand-2x3':1 };

  /* Size code and category letter for a range. Both the exact SKU and every
     fallback candidate must go through these, or the fallbacks address a SKU
     that was never rendered: Cocobella Grand, Salthrop Medium and all three
     sectionals resolve to a different base from the plain SIZE_CODES lookup. */
  function sizeCodeFor(range, sizeKey) {
    var perRange = RANGE_SIZE_CODES[range];
    return (perRange && perRange[sizeKey]) || SIZE_CODES[sizeKey] || '25';
  }
  function categoryFor(sizeKey, defaultCategory) {
    return MODULAR_SIZES[sizeKey] ? 'M' : defaultCategory;
  }

  var CUSHION_CODES = { 'knife-edge':'K', 'knife':'K', 'box-edge-big':'B', 'box-edge':'B', 'box':'B', 'box-edge-scatter':'S', 'box-edge-fixed':'F', 'scatter':'S', 'fixed':'F' };
  var COVER_CODES = { 'fixed':'1', 'loose':'2', 'loose-standard':'2', 'loose-box-pleat':'3', 'boxpleat':'3', 'box-pleat':'3' };
  var PIPING_CODES = { 'self':'1', 'self-flat':'1', 'self-round':'2', 'contrast':'3', 'contrast-flat':'3', 'contrast-round':'4', 'oxford':'5', 'soft-edge':'6' };
  var VIEW_CODES = { 'front':'FR', 'side':'SI', 'angle':'SI', 'angle-left':'SI', 'angle-right':'SI', 'rear':'RE', 'detail':'DE', 'top':'TO' };
  var AVAILABLE_VIEWS = ['FR', 'SI', 'RE', 'DE'];
  /* Sectionals are rendered differently: the 2x3 exists only as a left- or
     right-hand-facing view (LHF / RHF) of the same SKU, and the 3x3 only
     as a side view. Asking for FR/SI/RE/DE on these returns nothing. */
  var SIZE_VIEWS = {
    'left-hand-2x3':  ['LHF'],
    'right-hand-2x3': ['RHF'],
    'corner-3x3':     ['SI']
  };
  function viewsForConfig(config) {
    var v = SIZE_VIEWS[String((config && config.size) || '').toLowerCase()];
    return v || AVAILABLE_VIEWS;
  }

  /* Model defaults */
  var MODEL_DEFAULTS = {
    coco:      { cushion: 'K', cover: '2', piping: '1' },
    barton:    { cushion: 'B', cover: '2', piping: '1' },
    henrietta: { cushion: 'F', cover: '1', piping: '1' },
    isobel:    { cushion: 'B', cover: '1', piping: '1' },
    felix:     { cushion: 'F', cover: '1', piping: '1' },
    sophia:    { cushion: 'F', cover: '1', piping: '1' },
    /* Read off the render manifest rather than assumed. Salthrop has two
       cushion styles in the renders (B and S); B is used here as the
       default until we confirm which should lead. */
    cocobella: { cushion: 'K', cover: '2', piping: '1' },
    'felix-jl':{ cushion: 'F', cover: '2', piping: '2' },
    salthrop:  { cushion: 'B', cover: '2', piping: '2' }
  };

  SC.imageResolver = {

    config: {
      /* Set to your R2 custom domain URL */
      cdnBase: 'https://cdn.sophieconran.com/renders',

      /* Default fabric code (Snow Drop) */
      defaultFabricCode: 'O14',

      /* Per-model base render shown on configurator load, before the user
         makes a selection. Keyed by model (lowercase). Each is a real CDN
         render in the standard pipeline, so it flows through the same
         transform wrapper as everything else. Add a model's render here once
         it is uploaded; until then getBaseRender() falls back to a neutral
         placeholder so nothing breaks. This keeps one `sofa` template for all
         models while each shows its own base image on load. */
      baseRenders: {
        'coco': 'KCOS25K-O1421-FR-Coco-2.5 Seater-Capelo Snow Drop.png'
        /* 'barton':    '...',  once uploaded */
        /* 'henrietta': '...',  */
        /* 'isobel':    '...',  */
        /* 'felix':     '...',  */
        /* 'sophia':    '...',  */
      },

      /* Country code */
      country: 'K',

      /* Product category (standard sofa) */
      category: 'S',

      /* Image format */
      format: 'png',

      /* Cloudflare Image Transformations: rewrite CDN URLs through
         /cdn-cgi/image/<opts>/ so Cloudflare serves format-optimised
         (AVIF/WebP), compressed versions. These renders display large, so the
         width is generous; the real win here is format=auto + quality, not
         heavy downsizing. Set transform.enabled=false to serve originals. */
      transform: {
        enabled: true,
        width: 1600,
        quality: 82,
        format: 'auto'
      },

      /* Placeholder */
      placeholder: 'data:image/svg+xml,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 800">' +
        '<rect fill="#f0ede6" width="800" height="800"/>' +
        '<text x="400" y="400" font-family="Georgia" font-size="24" fill="#b08c47" ' +
        'text-anchor="middle" dominant-baseline="middle" font-style="italic">' +
        'Image coming soon</text></svg>'
      )
    },

    /* ── Build a SKU from configuration ── */
    buildSku: function(config) {
      var range = (config.range || 'coco').toLowerCase();
      var defaults = MODEL_DEFAULTS[range] || MODEL_DEFAULTS.coco;

      var rangeCode = RANGE_CODES[range] || 'CO';
      var _sizeKey = (config.size || '2.5-seater').toLowerCase();
      var sizeCode = sizeCodeFor(range, _sizeKey);
      var cushionCode = CUSHION_CODES[(config.cushion || '').toLowerCase()] || defaults.cushion;
      var coverCode = COVER_CODES[(config.cover || '').toLowerCase()] || defaults.cover;
      var pipingCode = PIPING_CODES[(config.piping || '').toLowerCase()] || defaults.piping;

      /* Fabric code: either passed directly (e.g. 'O14') or resolved from name */
      var fabricCode = config.fabricCode || this._resolveFabricCode(config.fabric) || this.config.defaultFabricCode;

      /* SKU: K + CO + S + 25 + K + - + O14 + 2 + 1 */
      var categoryCode = categoryFor(_sizeKey, this.config.category);

      return this.config.country + rangeCode + categoryCode + sizeCode + cushionCode +
             '-' + fabricCode + coverCode + pipingCode;
    },

    /* ── Wrap a CDN URL in a Cloudflare image transform ── */
    withTransform: function(url) {
      var tf = this.config.transform;
      if (!tf || !tf.enabled || !url || !/^https?:\/\//i.test(url)) return url;
      if (url.indexOf('/cdn-cgi/image/') !== -1) return url;
      var m = url.match(/^(https?:\/\/[^\/]+)(\/.*)$/i);
      if (!m) return url;
      var opts = [];
      if (tf.width) opts.push('width=' + tf.width);
      if (tf.quality) opts.push('quality=' + tf.quality);
      if (tf.format) opts.push('format=' + tf.format);
      if (!opts.length) return url;
      return m[1] + '/cdn-cgi/image/' + opts.join(',') + m[2];
    },

    /* ── Build a CDN URL from SKU + view ── */
    buildUrl: function(sku, view) {
      if (!this.config.cdnBase) return null;
      var viewCode = VIEW_CODES[(view || 'front').toLowerCase()] || 'FR';
      var prefix = sku + '-' + viewCode;
      var cdn = this.config.cdnBase.replace(/\/$/, '');

      /* 1. Check manifest (most reliable - uses actual filenames) */
      if (this._manifest && this._manifest[prefix]) {
        return this.withTransform(cdn + '/' + encodeURIComponent(this._manifest[prefix]));
      }

      /* 2. Deterministic fallback (may not match all fabric names) */
      var decoded = this.decodeSku(sku);
      if (!decoded) return this.withTransform(cdn + '/' + prefix + '.' + this.config.format);
      var modelName = this._rangeDisplayName(decoded.range);
      var sizeName = this._sizeCodeToName(sku.substring(4, 6), decoded.range);
      var fabricName = this._fabricCodeToName(decoded.fabricCode);
      var filename = sku + '-' + viewCode + '-' + modelName + '-' + sizeName + '-' + fabricName;
      this._lastAltUrl = this.withTransform(cdn + '/' + encodeURIComponent(filename + '.jpg'));
      return this.withTransform(cdn + '/' + encodeURIComponent(filename + '.' + this.config.format));
    },

    /* ── Manifest: maps SKU-VIEW prefix to actual filename ── */
    _manifest: null,
    _manifestLoaded: false,

    _loadManifest: function() {
      if (this._manifestLoaded) return;
      this._manifestLoaded = true;

      /* Use the embedded JS manifest (no CORS issues) */
      if (SC.renderManifest && Object.keys(SC.renderManifest).length > 0) {
        this._manifest = SC.renderManifest;
      } else {
        this._manifest = {};
      }
    },

    /* ── Reverse lookup: size code to display name ── */
    _sizeCodeToName: function(code, range) {
      /* John Lewis name their sizes differently from us: Snuggler, Small,
         Medium, Large, Grand rather than seat counts. Same underlying
         codes, different words in the filename. */
      var JL = { 'cocobella': 1, 'salthrop': 1, 'felix-jl': 1 };
      if (range && JL[range]) {
        var jlMap = { '10':'Armchair', '15':'Snuggler', '20':'Small',
                      '25':'Medium', '30':'Large', '35':'Grand', '40':'Grand',
                      '33':'3x3 Sectional', 'LA':'Left Hand 2x3', 'LN':'Right Hand 2x3' };
        if (jlMap[code]) return jlMap[code];
      }
      var map = { '10':'Armchair', '15':'Loveseat', '20':'2 Seater', '25':'2.5 Seater',
                  '30':'3 Seater', '35':'3.5 Seater', '37':'3 Seater 2 Cushion',
                  '40':'4 Seater', '42':'4 Seater 2 Cushion' };
      return map[code] || code;
    },

    /* ── Reverse lookup: fabric code to Collection Colorway ── */
    _fabricCodeToName: function(code) {
      var map = {
        /* John Lewis fabrics, from the JLP master sheet + SKU reference.
           Without these the code itself landed in the render filename. */
        'J04':'Bruges Navy', 'J05':'Bruges Sage', 'J06':'Bruges Sand',
        'J07':'Suzy Olive', 'J08':'Suzy Denim', 'J09':'Suzy Natural',
        'J10':'Beauville Spring Green', 'J11':'Beauville Cornish Blue',
        'O23':'Pinstripe Natural', 'O24':'Pinstripe Petunia', 'O25':'Pinstripe Nutmeg',
        'O26':'Tasman Natural',
        'A01':'Wildwood Duck Egg', 'A02':'Wildwood Ivory', 'A03':'Wildwood Twig',
        'A04':'Peony Summer Sky', 'A05':'Midsummer Sky', 'A06':'Midsummer Stone',
        'A07':'Midsummer Faded Blue', 'A08':'Pavilion Otter Denim',
        'A09':'Pelham Clementine', 'A10':'Pelham Moss', 'A11':'Pelham Denim', 'A12':'Pelham Peacock',
        'A13':'Goat Las Salinas 4',
        'B01':'Beatrice Barley', 'B02':'Beatrice Camelia', 'B03':'Beatrice Midnight',
        'B04':'Beatrice Nutmeg', 'B05':'Beatrice Olive',
        'B06':'Froisse Cerulean', 'B07':'Froisse Chalk', 'B08':'Froisse Hemp',
        'B09':'Froisse Khaki', 'B10':'Froisse Mole', 'B11':'Froisse Pebble',
        'B12':'Froisse Putty', 'B13':'Froisse Sisal',
        'C01':'Berrington',
        'D01':'Varese Fuchsia', 'D02':'Varese Blossom', 'D03':'Varese Alchemilla',
        'D04':'Varese Terracotta', 'D05':'Varese Grass', 'D06':'Cerise', 'D07':'Chalk', 'D08':'Putty',
        'G01':'Nympheus Teal Green', 'G02':'Nympheus Stone Pistachio', 'G03':'Nympheus Aqua Teal',
        'G04':'Magnolia Cream Blue', 'G05':'Little Magnolia Pink Orange',
        'G06':'Little Magnolia Espresso', 'G07':'Little Magnolia Emerald',
        'L01':'English Garden Kitty Spring Green', 'L02':'English Garden Kitty Blue Green',
        'L03':'English Garden Kitty Indian Summer', 'L04':'English Garden Albertine Grey Skies',
        'L05':'English Garden Albertine Classic Rose', 'L06':'English Garden Albertine Coral Orange',
        'L07':'Belleville Miji Juniper', 'L08':'Omega IV Folly', 'L09':'Omega IV Carnelian',
        'L10':'Omega IV Pastel Blue', 'L11':'Omega IV Lagoon', 'L12':'Omega IV Rosewood',
        'L13':'Omega IV Winter Moss', 'L14':'Omega IV Pine', 'L15':'Omega IV Grass',
        'L16':'Omega IV Pillarbox', 'L17':'Omega IV Parakeet',
        'L18':'Elba Cerulean', 'L19':'Elba Hemp', 'L20':'Elba Midnight', 'L21':'Elba Sage',
        'O01':'Tasman Coastal Blue', 'O02':'Tasman Petunia', 'O03':'Tasman Woodland Stripe',
        'O04':'Tasman Caramel Stripe', 'O05':'Tasman Sea Holly', 'O06':'Tasman Garden Grey',
        'O07':'Tasman Dandelion', 'O08':'Tasman Spring Green', 'O09':'Tasman Cornish Blue',
        'O10':'Tasman Narrow Chocolate', 'O11':'Tasman Chocolate', 'O12':'Tasman Blossom',
        'O13':'Cool Coconut', 'O14':'Capelo Snow Drop', 'O15':'Capelo Sea Salt',
        'O16':'Capelo Rosedale', 'O17':'Capelo Inkspot', 'O18':'Columbus Evergreen',
        'O19':'Columbus Marigold', 'O20':'Columbus Potting Moss', 'O21':'Columbus Roselette',
        'O22':'Columbus Sunflower',
        'P01':'Picocesto Selenite', 'P02':'Picocesto Sunstone',
        'J01':'Boho Cloud', 'J02':'Boho Denim', 'J03':'Boho Parchment'
      };
      return map[code] || code;
    },

    /* Display name used in render filenames. _titleCase alone produced
       'Felix-jl' for the John Lewis Felix, which matches nothing on the
       CDN. */
    _rangeDisplayName: function(range) {
      var map = {
        'cocobella': 'Cocobella',
        'salthrop':  'Salthrop',
        'felix-jl':  'Felix (JLP)'
      };
      return map[range] || this._titleCase(range);
    },

    _titleCase: function(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },

    /* ── Get the best image for a configuration ── */
    getImage: function(config, callback) {
      var self = this;

      if (!this.config.cdnBase) {
        var legacy = this._getLegacyImage(config);
        callback(legacy || this.config.placeholder, 'legacy', null);
        return;
      }

      /* Load manifest on first call (synchronous, from embedded JS) */
      this._loadManifest();

      this._resolveImage(config, callback);
    },

    _resultCache: {},
    _resolveImage: function(config, callback) {
      var self = this;
      var view = config.view || 'front';
      var exactSku = this.buildSku(config);
      var range = (config.range || 'coco').toLowerCase();
      var defaults = MODEL_DEFAULTS[range] || MODEL_DEFAULTS.coco;
      var _sizeKey = (config.size || '2.5-seater').toLowerCase();
      var sizeCode = sizeCodeFor(range, _sizeKey);
      var fabricCode = config.fabricCode || this._resolveFabricCode(config.fabric) || this.config.defaultFabricCode;

      /* Memoise the resolved result per config so re-selecting a fabric the
         user has already viewed is instant (no re-probing the network). */
      var cacheKey = [range, config.size, fabricCode, config.cushion, config.cover, config.piping, view].join('|');
      var cached = this._resultCache[cacheKey];
      if (cached) {
        callback(cached.url, cached.matchType, cached.sku);
        return;
      }

      /* Build a prioritised list of SKU fallbacks:
         1. Exact config
         2. Same fabric + cushion, default cover/piping
         3. Same fabric, default cushion/cover/piping
         4. Default fabric, selected cushion, default cover/piping
         5. Default fabric, selected cushion/cover/piping
         6. Full default */
      var skuCandidates = [];
      var candidateLabels = [];

      var cushionCode = CUSHION_CODES[(config.cushion || '').toLowerCase()] || defaults.cushion;
      var coverCode = COVER_CODES[(config.cover || '').toLowerCase()] || defaults.cover;
      var pipingCode = PIPING_CODES[(config.piping || '').toLowerCase()] || defaults.piping;
      var base = this.config.country + (RANGE_CODES[range] || 'CO') +
                 categoryFor(_sizeKey, this.config.category) + sizeCode;

      /* 1. Exact */
      skuCandidates.push(exactSku);
      candidateLabels.push('exact');

      /* 2. Same fabric + cushion, default cover/piping */
      var sameFabCushDefStyle = base + cushionCode + '-' + fabricCode + defaults.cover + defaults.piping;
      if (skuCandidates.indexOf(sameFabCushDefStyle) === -1) {
        skuCandidates.push(sameFabCushDefStyle);
        candidateLabels.push('fabric-cushion-default-piping');
      }

      /* 3. Same fabric, all defaults */
      var sameFabricDefault = base + defaults.cushion + '-' + fabricCode + defaults.cover + defaults.piping;
      if (skuCandidates.indexOf(sameFabricDefault) === -1) {
        skuCandidates.push(sameFabricDefault);
        candidateLabels.push('fabric-default-style');
      }

      /* 4. Default fabric, selected cushion, default cover/piping */
      var defFabSelCush = base + cushionCode + '-' + this.config.defaultFabricCode + defaults.cover + defaults.piping;
      if (skuCandidates.indexOf(defFabSelCush) === -1) {
        skuCandidates.push(defFabSelCush);
        candidateLabels.push('default-fabric-selected-cushion');
      }

      /* 5. Default fabric, selected cushion/cover/piping */
      var defFabSelStyle = base + cushionCode + '-' + this.config.defaultFabricCode + coverCode + pipingCode;
      if (skuCandidates.indexOf(defFabSelStyle) === -1) {
        skuCandidates.push(defFabSelStyle);
        candidateLabels.push('default-fabric-selected-style');
      }

      /* 6. Full default */
      var fullDefault = base + defaults.cushion + '-' + this.config.defaultFabricCode + defaults.cover + defaults.piping;
      if (skuCandidates.indexOf(fullDefault) === -1) {
        skuCandidates.push(fullDefault);
        candidateLabels.push('full-default');
      }

      var viewCode = VIEW_CODES[(view || 'front').toLowerCase()] || 'FR';

      /* FAST PATH: if any candidate's SKU-VIEW prefix is in the manifest, that
         filename is known to exist, so use the first (highest-priority) one
         directly with no network probe. */
      if (this._manifest) {
        for (var k = 0; k < skuCandidates.length; k++) {
          var prefix = skuCandidates[k] + '-' + viewCode;
          if (this._manifest[prefix]) {
            var manifestUrl = this.buildUrl(skuCandidates[k], view);
            self._resultCache[cacheKey] = { url: manifestUrl, matchType: candidateLabels[k], sku: skuCandidates[k] };
            callback(manifestUrl, candidateLabels[k], skuCandidates[k]);
            return;
          }
        }
      }

      /* Build URL candidates: each SKU in .png then .jpg, remembering which SKU
         and label each URL came from. */
      var candidates = [];
      var labels = [];
      var skus = [];
      for (var s = 0; s < skuCandidates.length; s++) {
        var url1 = this.buildUrl(skuCandidates[s], view);
        var url2 = this._lastAltUrl;
        if (url1) { candidates.push(url1); labels.push(candidateLabels[s]); skus.push(skuCandidates[s]); }
        if (url2) { candidates.push(url2); labels.push(candidateLabels[s]); skus.push(skuCandidates[s]); }
      }

      /* Probe all candidates in parallel, then pick the highest-priority one
         that exists. Parallel probing turns N sequential network round-trips
         into roughly one round-trip of wall-time, which is the main speed win
         when no exact render exists for a fabric. */
      var pending = candidates.map(function(url) { return self._checkUrl(url); });
      Promise.all(pending).then(function(results) {
        for (var i = 0; i < results.length; i++) {
          if (results[i]) {
            self._resultCache[cacheKey] = { url: candidates[i], matchType: labels[i], sku: skus[i] };
            callback(candidates[i], labels[i], skus[i]);
            return;
          }
        }
        var legacy = self._getLegacyImage(config);
        var fallbackUrl = legacy || self.config.placeholder;
        self._resultCache[cacheKey] = { url: fallbackUrl, matchType: 'legacy', sku: null };
        callback(fallbackUrl, 'legacy', null);
      });
    },

    /* ── Get available views for a SKU ── */
    getAvailableViews: function(config, callback) {
      var self = this;
      var viewCodes = ['front', 'side', 'rear', 'detail'];

      /* First find which SKU actually resolved for the front view
         (this is the best available match). Then check other views
         for that same SKU. */
      this.getImage(config, function(heroUrl, matchType, matchedSku) {
        if (!matchedSku) { callback([]); return; }

        var results = [];
        var checked = 0;

        viewCodes.forEach(function(view) {
          var url = self.buildUrl(matchedSku, view);
          var altUrl = self._lastAltUrl;

          if (!url) { checked++; checkDone(); return; }

          self._checkUrl(url).then(function(exists) {
            if (exists) {
              results.push({ view: view, url: url });
              checked++;
              checkDone();
            } else if (altUrl) {
              self._checkUrl(altUrl).then(function(altExists) {
                if (altExists) results.push({ view: view, url: altUrl });
                checked++;
                checkDone();
              });
            } else {
              checked++;
              checkDone();
            }
          });
        });

        function checkDone() {
          if (checked === viewCodes.length) {
            var order = { front: 0, side: 1, rear: 2, detail: 3 };
            results.sort(function(a, b) { return (order[a.view] || 9) - (order[b.view] || 9); });
            callback(results);
          }
        }
      });
    },

    /* ── Per-model base render for the configurator's load-time image ──
       Looks up the model in config.baseRenders and returns the CDN render
       through the transform wrapper. Falls back to the neutral placeholder
       if the model has no render uploaded yet, so the page never breaks. */
    getBaseRender: function(model) {
      var key = (model || '').toString().toLowerCase().trim();
      var file = this.config.baseRenders && this.config.baseRenders[key];
      if (!file || !this.config.cdnBase) return this.config.placeholder;
      var base = this.config.cdnBase.replace(/\/$/, '');
      /* Encode the filename (the renders contain spaces) and wrap in the
         Cloudflare transform, exactly like buildUrl does for SKU renders. */
      var raw = base + '/' + encodeURIComponent(file);
      return this.withTransform ? this.withTransform(raw) : raw;
    },

    /* ── Synchronous: get URL without 404 check (for immediate rendering) ── */
    getImageSync: function(config) {
      if (!this.config.cdnBase) {
        return this._getLegacyImage(config) || this.config.placeholder;
      }
      var sku = this.buildSku(config);
      return this.buildUrl(sku, config.view || 'front');
    },

    /* ── Get all gallery images for a configuration ── */
    getGallery: function(config, callback) {
      var self = this;

      if (!this.config.cdnBase) {
        /* Legacy: return whatever sc-images.js has */
        var entry = this._getLegacyEntry(config);
        if (entry && entry.angles) {
          callback(entry.angles.map(function(url, i) {
            return { view: AVAILABLE_VIEWS[i] || 'FR', url: url };
          }));
        } else {
          callback([{ view: 'FR', url: this._getLegacyImage(config) || this.config.placeholder }]);
        }
        return;
      }

      var sku = this.buildSku(config);
      var views = viewsForConfig(config);
      var results = [];
      var pending = views.length;

      views.forEach(function(viewCode, i) {
        var url = self.buildUrl(sku, Object.keys(VIEW_CODES).find(function(k) { return VIEW_CODES[k] === viewCode; }) || 'front');
        self._checkUrl(url).then(function(exists) {
          if (exists) results.push({ view: viewCode, url: url });
          pending--;
          if (pending === 0) {
            results.sort(function(a, b) {
              return views.indexOf(a.view) - views.indexOf(b.view);
            });
            if (results.length === 0) {
              /* Try fallback SKU */
              var fallbackUrl = self.getImageSync(config);
              results.push({ view: 'FR', url: fallbackUrl });
            }
            callback(results);
          }
        });
      });
    },

    /* ── Resolve fabric name to code ── */
    _fabricLookup: null,

    _resolveFabricCode: function(fabricHandle) {
      if (!fabricHandle) return null;

      /* Direct handle-to-code lookup (comprehensive, from SSOT) */
      if (!this._handleToCode) {
        this._handleToCode = {
          /* John Lewis fabrics. Without these, buildSku fell through to the
             default fabric code, so every John Lewis render URL asked for
             the wrong fabric regardless of what the customer picked. */
          'bruges-navy':'J04','bruges-sage':'J05','bruges-sand':'J06',
          'suzy-olive':'J07','suzy-denim':'J08','suzy-natural':'J09',
          'beauville-spring-green':'J10','beauville-cornish-blue':'J11',
          'coastal-blue-stripe':'O01','petunia-stripe':'O02',
          'spring-green-stripe':'O08','cornish-blue-stripe':'O09',
          'pinstripe-natural':'O23','pinstripe-petunia':'O24','pinstripe-nutmeg':'O25',
          'natural-stripe':'O26',
          'wildwood-duck-egg':'A01','wildwood-ivory':'A02','wildwood-twig':'A03',
          'wild-wood-duck-egg':'A01','wild-wood-ivory':'A02','wild-wood-twig':'A03',
          'peony-summer-sky':'A04','midsummer-sky':'A05','midsummer-stone':'A06',
          'midsummer-faded-blue':'A07','otter-denim':'A08',
          'pelham-clementine':'A09','pelham-moss':'A10','pelham-denim':'A11','pelham-peacock':'A12',
          'las-salinas-4':'A13','goat-las-salinas-4':'A13',
          'beatrice-barley':'B01','beatrice-camelia':'B02','beatrice-midnight':'B03',
          'beatrice-nutmeg':'B04','beatrice-olive':'B05',
          'froisse-cerulean':'B06','froisse-chalk':'B07','froisse-hemp':'B08',
          'froisse-khaki':'B09','froisse-mole':'B10','froisse-pebble':'B11',
          'froisse-putty':'B12','froisse-sisal':'B13',
          'berrington':'C01',
          'varese-fuchsia':'D01','varese-blossom':'D02','varese-alchemilla':'D03',
          'varese-terracotta':'D04','varese-grass':'D05',
          'cerise':'D06','brera-lino-cerise':'D06',
          'chalk':'D07','brera-lino-chalk':'D07',
          'putty':'D08','brera-lino-putty':'D08',
          'nympheus-teal-green':'G01','nympheus-stone-pistachio':'G02','nympheus-aqua-teal':'G03',
          'magnolia-cream-blue':'G04',
          'little-magnolia-pink-orange':'G05','little-magnolia-espresso':'G06','little-magnolia-emerald':'G07',
          'english-garden-kitty-spring-green':'L01','english-garden-kitty-blue-green':'L02',
          'english-garden-kitty-indian-summer':'L03',
          'english-garden-albertine-grey-skies':'L04','english-garden-albertine-classic-rose':'L05',
          'english-garden-albertine-coral-orange':'L06',
          'belleville-miji-juniper':'L07',
          'omega-folly':'L08','omega-iv-folly':'L08',
          'omega-carnelian':'L09','omega-iv-carnelian':'L09',
          'omega-pastel-blue':'L10','omega-iv-pastel-blue':'L10',
          'omega-lagoon':'L11','omega-iv-lagoon':'L11',
          'omega-rosewood':'L12','omega-iv-rosewood':'L12',
          'omega-winter-moss':'L13','omega-iv-winter-moss':'L13',
          'omega-pine':'L14','omega-iv-pine':'L14',
          'omega-grass':'L15','omega-iv-grass':'L15',
          'omega-pillarbox':'L16','omega-iv-pillarbox':'L16','omega-pillar-box':'L16',
          'omega-parakeet':'L17','omega-iv-parakeet':'L17',
          'elba-cerulean':'L18','elba-hemp':'L19','elba-midnight':'L20','elba-sage':'L21',
          'tasman-coastal-blue':'O01','coastal-blue':'O01',
          'tasman-petunia':'O02','petunia':'O02',
          'tasman-woodland-stripe':'O03','woodland-stripe':'O03',
          'tasman-caramel-stripe':'O04','caramel-stripe':'O04',
          'tasman-sea-holly':'O05','sea-holly':'O05',
          'tasman-garden-grey':'O06','garden-grey':'O06',
          'tasman-dandelion':'O07','dandelion':'O07',
          'tasman-spring-green':'O08','spring-green':'O08',
          'tasman-cornish-blue':'O09','cornish-blue':'O09',
          'tasman-narrow-chocolate':'O10','narrow-chocolate':'O10',
          'tasman-chocolate':'O11','chocolate':'O11',
          'tasman-blossom':'O12','blossom':'O12',
          'cool-coconut':'O13',
          'capelo-snow-drop':'O14','capelo-snowdrop':'O14','snow-drop':'O14','snowdrop':'O14',
          'capelo-sea-salt':'O15','sea-salt':'O15',
          'capelo-rosedale':'O16','rosedale':'O16',
          'capelo-inkspot':'O17','inkspot':'O17',
          'columbus-evergreen':'O18','evergreen':'O18',
          'columbus-marigold':'O19','marigold':'O19',
          'columbus-potting-moss':'O20','potting-moss':'O20',
          'columbus-roselette':'O21','roselette':'O21',
          'columbus-sunflower':'O22','sunflower':'O22',
          'picocesto-selenite':'P01','selenite':'P01',
          'picocesto-sunstone':'P02','sunstone':'P02',
          'boho-cloud':'J01','boho-denim':'J02','boho-parchment':'J03'
        };
      }

      var key = fabricHandle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      if (this._handleToCode[key]) return this._handleToCode[key];

      /* Try partial match: strip common prefixes */
      var parts = key.split('-');
      for (var i = 1; i < parts.length; i++) {
        var partial = parts.slice(i).join('-');
        if (this._handleToCode[partial]) return this._handleToCode[partial];
      }

      /* Hyphenation-insensitive match: collapse all hyphens and compare
         against a hyphen-collapsed version of every map key. Catches
         variants like "wild-wood-ivory" vs "wildwood-ivory" without needing
         an explicit alias for each one. Built once and cached. */
      if (!this._collapsedMap) {
        this._collapsedMap = {};
        for (var mk in this._handleToCode) {
          if (Object.prototype.hasOwnProperty.call(this._handleToCode, mk)) {
            this._collapsedMap[mk.replace(/-/g, '')] = this._handleToCode[mk];
          }
        }
      }
      var collapsed = key.replace(/-/g, '');
      if (this._collapsedMap[collapsed]) return this._collapsedMap[collapsed];

      console.warn('[SC.imageResolver] No fabric code for handle:', fabricHandle);
      return null;
    },

    /* ── URL existence check with cache ── */
    _urlCache: {},
    _checkUrl: function(url) {
      if (!url) return Promise.resolve(false);
      if (this._urlCache[url] !== undefined) return Promise.resolve(this._urlCache[url]);
      var self = this;
      return new Promise(function(resolve) {
        var img = new Image();
        img.onload = function() { self._urlCache[url] = true; resolve(true); };
        img.onerror = function() { self._urlCache[url] = false; resolve(false); };
        img.src = url;
      });
    },

    /* ── Legacy sc-images.js lookup ── */
    _getLegacyEntry: function(config) {
      if (!SC.images || !SC.images.product) return null;
      var range = (config.range || 'coco').toLowerCase();
      var size = (config.size || '2.5-seater').replace(/[^0-9.]/g, '').replace('.', '_');
      var key = range + '_' + size;
      var entry = SC.images.product[key];
      if (!entry) {
        var fallbacks = [range + '_2_5', range + '_3', range + '_2'];
        for (var i = 0; i < fallbacks.length; i++) {
          if (SC.images.product[fallbacks[i]]) return SC.images.product[fallbacks[i]];
        }
      }
      return entry || null;
    },

    _getLegacyImage: function(config) {
      var entry = this._getLegacyEntry(config);
      if (!entry) return null;

      /* Try fabric-specific image */
      if (entry.byFabric && config.fabric) {
        var fabricKey = (config.fabric || '').toLowerCase().replace(/\s+/g, '-');
        if (entry.byFabric[fabricKey]) return entry.byFabric[fabricKey];
        if (config.fabricCode && entry.byFabric[config.fabricCode]) return entry.byFabric[config.fabricCode];
      }

      return entry.hero || (entry.angles && entry.angles[0]) || null;
    },

    /* ── Decode a SKU string back to configuration ── */
    decodeSku: function(sku) {
      if (!sku || sku.length < 8) return null;
      var parts = sku.split('-');
      if (parts.length < 2) return null;
      var left = parts[0];
      var right = parts[1];

      var rangeCode = left.substring(1, 3);
      var sizeCode = left.substring(4, 6);
      var cushionCode = left.substring(6, 7);

      var fabricCode = right.substring(0, 3);
      var coverCode = right.substring(3, 4);
      var pipingCode = right.substring(4, 5);

      /* Reverse lookups */
      var rangeMap = {}; for (var r in RANGE_CODES) rangeMap[RANGE_CODES[r]] = r;
      var sizeMap = {}; for (var s in SIZE_CODES) sizeMap[SIZE_CODES[s]] = s;
      var cushionMap = {}; for (var c in CUSHION_CODES) cushionMap[CUSHION_CODES[c]] = c;
      var coverMap = {}; for (var v in COVER_CODES) coverMap[COVER_CODES[v]] = v;

      return {
        range: rangeMap[rangeCode] || rangeCode,
        size: sizeMap[sizeCode] || sizeCode,
        cushion: cushionMap[cushionCode] || cushionCode,
        fabricCode: fabricCode,
        cover: coverMap[coverCode] || coverCode,
        piping: pipingCode
      };
    }
  };
})();
